// Phase 34 · 20a — Inbox.
//
// One queue, ranked by deadline. The channel tabs are FILTERS, not
// destinations: ALL is the default and the point, and the per-channel tabs
// exist for someone who owns one channel — not to restore the old split.
//
// The filter goes to the server (`channel` is a real query param on /inbox),
// so paging and ordering stay correct. Nothing here fetches three endpoints
// and merges them.
//
// Nothing the old screen could do was dropped: search, the status/priority
// filter panel, email sync, refresh, pagination and the role-aware views all
// survive — the views moved from a left sidebar into the second tab group,
// and the channel filter moved out of the panel into the first.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Mail, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { useInboxStore } from '@stores/inboxStore';
import { useAuthStore } from '@stores/authStore';
import { canChangeAssignee } from '@/features/tickets/assignmentPermissions';
import { webSocketService } from '@hooks/useWebSocket';
import { inboxService, ticketService } from '@services/index';
import type { InboxItem, InboxView } from '@services/inbox.service';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import {
  CHANNEL_FILTER,
  CHANNEL_GLYPH,
  byNewest,
  bySlaThenReceived,
  channelTally,
  isBreached,
  queueChannel,
  receivedLabel,
  requesterLine,
  slaLabel,
  slaTone,
} from './inboxQueue';

const ADMIN_ROLES = ['admin', 'owner'];
const SUPERVISOR_ROLES = ['supervisor'];

/** One strip, two groups: what the row IS, then whose it is. */
type ChannelTab = 'all' | 'email' | 'chat' | 'voice';
type ScopeTab = 'mine' | 'unassigned' | 'breached';
type Tab = ChannelTab | ScopeTab;

const CHANNEL_TABS: ChannelTab[] = ['all', 'email', 'chat', 'voice'];
const SCOPE_TABS: ScopeTab[] = ['mine', 'unassigned', 'breached'];
const SCOPE_VIEW: Record<ScopeTab, InboxView> = {
  mine: 'assigned_to_me',
  unassigned: 'unassigned',
  breached: 'sla_breach',
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];
const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = {
  borderRadius: 10,
  boxShadow: 'var(--co-shadow-card)',
};
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'flex h-[34px] flex-none items-center rounded-lg bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** Header-only variants: the header's controls take the Board's 10px radius. */
const HEADER_CONTROL = CONTROL.replace('rounded-lg', 'rounded-[10px]');
const HEADER_PRIMARY = PRIMARY.replace('rounded-lg', 'rounded-[10px]');

export default function InboxPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const userRole = user?.role || 'agent';
  const isLead = ADMIN_ROLES.includes(userRole) || SUPERVISOR_ROLES.includes(userRole);

  const [searchInput, setSearchInput] = useState('');
  const [tab, setTab] = useState<Tab>(isLead ? 'all' : 'mine');
  const [showFilters, setShowFilters] = useState(false);
  const [busy, setBusy] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const didInit = useRef(false);
  // True while an AUTO refresh is in flight: those run silently, so an empty
  // view doesn't flicker to the spinner and the Refresh button doesn't spin
  // every 30s. A manual Refresh / tab switch still shows loading as before.
  const backgroundRefresh = useRef(false);

  const {
    items,
    stats,
    total,
    page,
    totalPages,
    selectedId,
    filters,
    isLoading,
    isLoadingStats,
    isSyncing,
    syncResult,
    fetchInbox,
    fetchStats,
    setView,
    setQuery,
    setFilters,
    setSearchQuery,
    setPage,
    selectItem,
    markAsRead,
    refreshInbox,
    syncEmails,
  } = useInboxStore();

  // An agent has no "all" view — the store's default would come back empty, so
  // the default tab is theirs.
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (!isLead) setView('assigned_to_me');
    fetchInbox();
    fetchStats();
  }, [isLead, setView, fetchInbox, fetchStats]);

  // Keep the queue current while the page is open. New emails/tickets used to
  // appear only after leaving and re-entering the Inbox. Live ticket events
  // trigger a debounced refresh; a 30s poll backs that up (email-created
  // tickets don't always reach the socket); returning to the browser tab
  // refreshes at once. All of it re-runs the current tab/filter/search query,
  // and is skipped while the browser tab is hidden.
  useEffect(() => {
    const refreshQuietly = async () => {
      backgroundRefresh.current = true;
      try {
        await refreshInbox();
      } finally {
        backgroundRefresh.current = false;
      }
    };
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const refreshSoon = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        if (document.visibilityState === 'visible') void refreshQuietly();
      }, 1500);
    };
    const unsubs = [
      webSocketService.subscribe('ticket:created', refreshSoon),
      webSocketService.subscribe('ticket:updated', refreshSoon),
      webSocketService.subscribe('ticket:assigned', refreshSoon),
      webSocketService.subscribe('message:new', refreshSoon),
    ];
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') void refreshQuietly();
    }, 30_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshSoon();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubs.forEach((unsub) => unsub());
      clearInterval(poll);
      if (debounce) clearTimeout(debounce);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshInbox]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput, setSearchQuery]);

  useEffect(() => {
    if (!showFilters) return;
    const onClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setShowFilters(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showFilters]);

  const selectTab = useCallback(
    (next: Tab) => {
      setTab(next);
      const isScope = next in SCOPE_VIEW;
      setQuery({
        view: isScope ? SCOPE_VIEW[next as ScopeTab] : isLead ? 'all' : 'assigned_to_me',
        // A scope tab is channel-agnostic; ALL clears the channel filter.
        filters: {
          channel:
            isScope || next === 'all'
              ? undefined
              : CHANNEL_FILTER[next as Exclude<ChannelTab, 'all'>],
        },
      });
    },
    [isLead, setQuery]
  );

  // `?view=unassigned` deep-links straight to the Unassigned queue — whether
  // the page is mounting or already open. The param is then
  // dropped so it doesn't pin the tab on reload.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('view') !== 'unassigned') return;
    selectTab('unassigned');
    setSearchParams({}, { replace: true });
  }, [searchParams, selectTab, setSearchParams]);

  // The server orders the page by the chosen sort; this re-applies it on the
  // client (for SLA it also pins nulls last, which sla_priority does not promise).
  const sortMode = filters.sort === 'sla' ? 'sla' : 'newest';
  const rows = useMemo(
    () => [...items].sort(sortMode === 'sla' ? bySlaThenReceived : byNewest),
    [items, sortMode]
  );
  const selected = rows.find((i) => i.id === selectedId) ?? rows[0] ?? null;
  const breachedOnPage = rows.filter((i) => isBreached(i)).length;

  const activeFilterCount = [filters.status, filters.priority].reduce<number>(
    (n, v) => n + (Array.isArray(v) ? v.length : v ? 1 : 0),
    0
  );

  const toggleFilter = useCallback(
    (kind: 'status' | 'priority', value: string) => {
      const current = filters[kind];
      const list = Array.isArray(current) ? current : current ? [current] : [];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      setFilters({ [kind]: next.length ? next : undefined });
    },
    [filters, setFilters]
  );

  const isSelectedFilter = (kind: 'status' | 'priority', value: string) => {
    const current = filters[kind];
    return Array.isArray(current) ? current.includes(value) : current === value;
  };

  const openTicket = useCallback(
    (id: string) => {
      selectItem(id);
      navigate(`/tickets/${id}`);
    },
    [selectItem, navigate]
  );

  const handleSelect = useCallback(
    (item: InboxItem) => {
      selectItem(item.id);
      if ((item.unreadCount || 0) > 0) void markAsRead(item.id);
    },
    [selectItem, markAsRead]
  );

  const assignToMe = useCallback(async () => {
    if (!selected || !user?.id) return;
    setBusy(true);
    try {
      await ticketService.assignTicket(selected.id, user.id);
      toast.success('Assigned to you');
      await refreshInbox();
    } catch {
      toast.error('Could not assign this conversation');
    } finally {
      setBusy(false);
    }
  }, [selected, user?.id, toast, refreshInbox]);


  // Naming the channels is what tells a returning user this is not the email
  // inbox they remember.
  const subtitle = stats
    ? `${stats.unassigned} unassigned across email, chat and voice · ${stats.slaBreaching} breached`
    : sortMode === 'sla'
      ? 'Email, chat and voice in one queue, ranked by deadline'
      : 'Email, chat and voice in one queue, newest first';

  // Channel tabs count the whole queue, not the loaded page: the numbers come
  // from the server's own per-channel tally over the same set `total` counts.
  // Null (older BFF, or the call failed) means the tabs show no number rather
  // than a page-sized one dressed up as a queue-sized one.
  const channelCounts = useMemo(() => channelTally(stats?.byChannel), [stats?.byChannel]);

  const channelCount = (t: ChannelTab) => {
    if (isLoadingStats || !channelCounts) return null;
    if (t === 'all') return stats?.total ?? null;
    return channelCounts[t];
  };

  const scopeCount = (t: ScopeTab) =>
    !stats || isLoadingStats
      ? null
      : t === 'mine'
        ? stats.assignedToMe
        : t === 'unassigned'
          ? stats.unassigned
          : stats.slaBreaching;

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      {/* ---------- header card ---------- */}
      <header className={clsx(CARD_CLASS, 'flex-none px-5 py-[18px]')} style={{ ...CARD, borderRadius: 14 }}>
        {/* Row 1: title + subtitle, the utility cluster in the top-right corner. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              Inbox
            </h1>
            <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              {subtitle}
            </p>
          </div>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: channel/scope tabs on the left; search, sort and the header
            actions on the right, wrapping when space runs out. */}
        {/* One wrapping row. The tab strip keeps its natural width; when the
            row runs out (1024), search, sort and the actions move together to a
            right-aligned line below it. The actions used to sit in a separate
            non-shrinking column that the tabs slid underneath. */}
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
          <div className="contents">
            <div className="flex flex-none items-center gap-[6px] rounded-[10px] bg-[var(--co-selected)] p-1">
              {CHANNEL_TABS.map((t) => (
                <TabButton
                  key={t}
                  label={t}
                  count={channelCount(t)}
                  active={tab === t}
                  onClick={() => selectTab(t)}
                />
              ))}
              <div className="mx-1 h-4 w-px flex-none bg-[var(--co-line)]" />
              {SCOPE_TABS.map((t) => (
                <TabButton
                  key={t}
                  label={t}
                  count={scopeCount(t)}
                  active={tab === t}
                  onClick={() => selectTab(t)}
                />
              ))}
            </div>
            <div className="relative ml-auto w-[240px] max-w-full">
              <Search className="pointer-events-none absolute left-[11px] top-1/2 z-10 h-[14px] w-[14px] -translate-y-1/2 text-[var(--co-ink-3)]" />
              <input
                type="text"
                placeholder="Search the queue…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="h-[34px] w-full rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] pl-[32px] pr-8 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  aria-label="Clear search"
                  className="absolute right-[10px] top-1/2 z-10 -translate-y-1/2 text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
                >
                  <X className="h-[14px] w-[14px]" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setFilters({ sort: sortMode === 'newest' ? 'sla' : 'newest' })}
              title={sortMode === 'newest' ? 'Switch to SLA soonest' : 'Switch to newest first'}
              aria-label={`Sort: ${sortMode === 'newest' ? 'Newest first' : 'SLA soonest'}`}
              className={HEADER_CONTROL}
            >
              <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Sort
              </span>
              {sortMode === 'newest' ? 'Newest first' : 'SLA soonest'}
            </button>
            {syncResult && (
              <span
                className="co-mono flex items-center gap-[6px] rounded-[10px] border px-2 py-[7px] text-[9.5px] font-semibold uppercase tracking-[.11em]"
                style={
                  syncResult.success
                    ? {
                        borderColor: 'var(--co-green-line)',
                        background: 'var(--co-green-wash)',
                        color: 'var(--co-green)',
                      }
                    : {
                        borderColor: 'var(--co-burgundy-line)',
                        background: 'var(--co-burgundy-wash)',
                        color: 'var(--co-burgundy)',
                      }
                }
              >
                {syncResult.success
                  ? syncResult.ticketsCreated !== undefined
                    ? `${syncResult.ticketsCreated} tickets created`
                    : `${syncResult.messagesProcessed} synced`
                  : syncResult.error || 'Sync failed'}
              </span>
            )}

            </div>
            <div className="flex flex-none items-center gap-[10px]">
            <button type="button" onClick={syncEmails} disabled={isSyncing} className={HEADER_CONTROL}>
              <Mail className={clsx('h-[14px] w-[14px]', isSyncing && 'animate-pulse')} />
              {isSyncing ? 'Syncing…' : 'Sync email'}
            </button>

            <div className="relative" ref={filterRef}>
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className={clsx(
                  HEADER_CONTROL,
                  (showFilters || activeFilterCount > 0) &&
                    'border-[var(--co-brass)] text-[var(--co-brass-ink)]'
                )}
              >
                <SlidersHorizontal className="h-[14px] w-[14px]" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="co-mono text-[10px] font-semibold text-[var(--co-brass-ink)]">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {showFilters && (
                <div
                  className={clsx(CARD_CLASS, 'absolute left-0 top-full z-50 mt-2 w-[300px] max-w-[calc(100vw-2rem)] p-4')}
                  style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
                >
                  <FilterGroup
                    label="Status"
                    options={STATUS_OPTIONS}
                    isOn={(v) => isSelectedFilter('status', v)}
                    onToggle={(v) => toggleFilter('status', v)}
                  />
                  <div className="mt-4">
                    <FilterGroup
                      label="Priority"
                      options={PRIORITY_OPTIONS}
                      isOn={(v) => isSelectedFilter('priority', v)}
                      onToggle={(v) => toggleFilter('priority', v)}
                    />
                  </div>
                  <p className="mt-4 border-t border-[var(--co-line-subtle)] pt-3 text-[11.5px] leading-[1.5] text-[var(--co-ink-3)]">
                    Channel is filtered by the tabs, so it is not repeated here.
                  </p>
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setFilters({ status: undefined, priority: undefined })}
                      className="co-mono mt-3 text-[10px] font-semibold uppercase tracking-[.11em] text-[var(--co-brass-ink)] hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              )}
            </div>

            <button type="button" onClick={refreshInbox} disabled={isLoading && !backgroundRefresh.current} className={HEADER_CONTROL}>
              <RefreshCw className={clsx('h-[14px] w-[14px]', isLoading && !backgroundRefresh.current && 'animate-spin')} />
              Refresh
            </button>

            <button type="button" onClick={() => navigate('/tickets/new')} className={HEADER_PRIMARY}>
              + New ticket
            </button>
            </div>
        </div>
      </header>

      {/* ---------- queue + reading pane ---------- */}
      <div className="flex min-h-0 flex-1 gap-[14px]">
        <section
          className={clsx(CARD_CLASS, 'flex w-[482px] flex-none flex-col overflow-hidden')}
          style={CARD}
        >
          <div className="co-mono flex flex-none items-center border-b border-[var(--co-line)] px-5 pb-[11px] pt-[14px] text-[9.5px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-3)]">
            <div className="min-w-0 flex-1">
              {tab === 'unassigned'
                ? 'Unassigned queue'
                : tab === 'mine'
                  ? 'My queue'
                  : tab === 'breached'
                    ? 'Breached queue'
                    : 'Queue'}
            </div>
            {/* the arrow sits on the column the rows are actually ordered by */}
            <div className="w-24 flex-none text-right text-[var(--co-ink)]">
              {sortMode === 'sla' ? 'SLA ↑' : 'Newest ↓'}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto co-scrollbar-none">
            {isLoading && !backgroundRefresh.current && rows.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="h-6 w-6 animate-spin text-[var(--co-ink-3)]" />
              </div>
            ) : rows.length === 0 ? (
              <p className="px-5 py-12 text-[13px] text-[var(--co-ink-3)]">
                {searchInput
                  ? 'Nothing in the queue matches that search.'
                  : tab === 'unassigned'
                    ? 'Nothing unassigned. Every conversation has an owner.'
                    : 'Nothing waiting in this view.'}
              </p>
            ) : (
              rows.map((i) => (
                <Row
                  key={i.id}
                  item={i}
                  selected={i.id === selected?.id}
                  onSelect={handleSelect}
                  onOpen={openTicket}
                />
              ))
            )}
          </div>

          <div
            className="flex flex-none flex-wrap items-center gap-[14px] border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-3"
            style={{ borderRadius: '0 0 9px 9px' }}
          >
            <span className="co-mono whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
              {rows.length} of {total} shown
              {breachedOnPage > 0 ? ` · ${breachedOnPage} breached here` : ''}
            </span>
            <div className="flex-1" />
            {totalPages > 1 ? (
              <div className="flex flex-none items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className={CONTROL}
                >
                  ←
                </button>
                <span className="co-mono text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className={CONTROL}
                >
                  →
                </button>
              </div>
            ) : (
              <span className="text-[12px] text-[var(--co-ink-3)]">
                One queue, three channels — a voicemail breaches the same way an email does.
              </span>
            )}
          </div>
        </section>

        {/* Agents can take an unassigned conversation, not one someone else owns. */}
        <ReadingPane
          item={selected}
          busy={busy}
          canAssign={Boolean(user?.id) && canChangeAssignee(user, selected?.assignee?.id)}
          assignedToMe={Boolean(user?.id) && selected?.assignee?.id === user?.id}
          onAssign={assignToMe}
          onOpen={openTicket}
        />
      </div>
    </div>
  );
}

function FilterGroup({
  label,
  options,
  isOn,
  onToggle,
}: {
  label: string;
  options: { value: string; label: string }[];
  isOn: (v: string) => boolean;
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
        {label}
      </span>
      <div className="mt-[9px] flex flex-wrap gap-2">
        {options.map((o) => {
          const on = isOn(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onToggle(o.value)}
              aria-pressed={on}
              className="rounded-md border px-[9px] py-[5px] text-[11.5px]"
              style={
                on
                  ? {
                      borderColor: 'var(--co-brass)',
                      background: 'var(--co-brass-wash)',
                      color: 'var(--co-brass-ink)',
                      fontWeight: 600,
                    }
                  : {
                      borderColor: 'var(--co-line-strong)',
                      background: 'var(--co-raised)',
                      color: 'var(--co-ink-2)',
                    }
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TabButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number | null;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      // The count describes the queue this tab selects, not the rows left
      // after the search box and the filter panel — say so rather than let a
      // filtered list read as a broken count.
      title={
        count === null || count === undefined
          ? undefined
          : `${count} in the ${label} queue — search and filters are not applied to this count`
      }
      className="co-mono flex items-center gap-[6px] rounded-lg px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em]"
      style={
        active
          ? {
              background: 'var(--co-raised)',
              color: 'var(--co-ink)',
              fontWeight: 600,
              boxShadow: 'var(--co-shadow)',
            }
          : { color: 'var(--co-ink-3)', fontWeight: 500 }
      }
    >
      {label}
      {count !== null && count !== undefined && (
        <span
          className="font-semibold"
          style={{ color: active ? 'var(--co-brass-ink)' : 'var(--co-ink-3)' }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function Row({
  item,
  selected,
  onSelect,
  onOpen,
}: {
  item: InboxItem;
  selected: boolean;
  onSelect: (item: InboxItem) => void;
  onOpen: (id: string) => void;
}) {
  const tone = slaTone(item);
  const channel = queueChannel(item.channel);
  const breached = isBreached(item);
  const unread = (item.unreadCount || 0) > 0;

  const slaColor =
    tone === 'danger'
      ? 'var(--co-burgundy)'
      : tone === 'warn'
        ? 'var(--co-terracotta-ink)'
        : tone === 'none'
          ? 'var(--co-ink-3)'
          : 'var(--co-ink-2)';

  // Voice is machine-coloured because a voicemail is a recording, not a
  // conversation — the same rule the rest of the product uses for --co-machine.
  const isVoice = channel === 'voice';

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      onDoubleClick={() => onOpen(item.id)}
      className="flex w-full gap-[11px] border-b border-[var(--co-line-subtle)] px-4 py-3 text-left hover:bg-[var(--co-selected)]"
      style={{
        borderLeft: `2px solid ${
          selected ? 'var(--co-brass)' : breached ? 'var(--co-burgundy)' : 'transparent'
        }`,
        background: selected ? 'var(--co-brass-wash)' : undefined,
      }}
    >
      <span
        className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] border text-[11px]"
        style={
          isVoice
            ? {
                borderColor: 'var(--co-machine-line)',
                background: 'var(--co-machine-wash)',
                color: 'var(--co-machine-ink)',
              }
            : {
                borderColor: 'var(--co-line-strong)',
                background: 'var(--co-raised)',
                color: 'var(--co-ink-3)',
              }
        }
      >
        {CHANNEL_GLYPH[channel]}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span
            className="min-w-0 truncate text-[13px] text-[var(--co-ink)]"
            style={{ fontWeight: unread ? 600 : 400 }}
          >
            {item.subject}
          </span>
          {/* weight AND a dot — hue is never the only unread signal */}
          {unread && (
            <span className="h-[6px] w-[6px] flex-none rounded-full bg-[var(--co-brass)]" />
          )}
        </span>
        <span className="co-mono mt-1 block truncate text-[10.5px] uppercase text-[var(--co-ink-3)]">
          {requesterLine(item)}
        </span>
      </span>

      <span className="w-24 flex-none text-right">
        <span
          className="co-mono block whitespace-nowrap text-[11px] font-semibold"
          style={{ color: slaColor }}
        >
          {slaLabel(item)}
        </span>
        <span className="co-mono mt-1 block text-[10px] text-[var(--co-ink-3)]">
          {receivedLabel(item.createdAt)}
        </span>
      </span>
    </button>
  );
}

/**
 * The pane prints the body as plain text, so markdown markers would otherwise
 * show as literal asterisks and backticks.
 */
function plainPreview(text: string): string {
  return text
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\*\*|~~|`/g, '')
    .replace(/^\s{0,3}(?:#{1,6}|>)\s+/gm, '')
    .replace(/(^|[\s(])\*(\S(?:[^*\n]*\S)?)\*(?=[\s).,!?:;]|$)/gm, '$1$2')
    .replace(/^(\s*)\*\s+/gm, '$1• ');
}

/**
 * The reading pane. The design's suggested-next-step block is cut: retrieval
 * does not run on inbox items here, and an empty machine-coloured box would
 * claim a capability the product does not have. Nothing else on the pane
 * depended on it.
 *
 * "Convert to ticket" is likewise absent — every item in this queue already IS
 * a ticket, so the action has nothing to convert. `Open ticket` takes its slot.
 */
function ReadingPane({
  item,
  busy,
  canAssign,
  assignedToMe = false,
  onAssign,
  onOpen,
}: {
  item: InboxItem | null;
  busy: boolean;
  canAssign: boolean;
  /** The ticket is already the viewer's: there is nothing to take. */
  assignedToMe?: boolean;
  onAssign: () => void;
  onOpen: (id: string) => void;
}) {
  if (!item) {
    return (
      <section
        className={clsx(CARD_CLASS, 'flex min-w-0 flex-1 items-center justify-center')}
        style={CARD}
      >
        <p className="max-w-[46ch] px-8 text-center text-[13px] leading-[1.6] text-[var(--co-ink-3)]">
          Pick a conversation to read it here. Double-click a row to open the full ticket.
        </p>
      </section>
    );
  }

  const breached = isBreached(item);
  const body = item.preview || item.description || item.lastMessage?.content;
  const requester = item.customer?.name || item.contactName || item.contactEmail || 'Unknown sender';

  return (
    <section
      className={clsx(CARD_CLASS, 'flex min-w-0 flex-1 flex-col overflow-hidden')}
      style={CARD}
    >
      <div className="flex-none border-b border-[var(--co-line-subtle)] px-[22px] pb-[14px] pt-[18px]">
        <div className="flex items-start gap-[14px]">
          <div className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[7px] bg-[var(--co-ink-3)] text-[15px] font-semibold text-[var(--co-ink-invert)]">
            {requester.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[21px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              {item.subject}
            </h2>
            <p className="co-mono mt-[6px] truncate text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
              {requester} · {queueChannel(item.channel)} ·{' '}
              {new Date(item.createdAt).toLocaleString([], {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
          <div className="flex flex-none items-center gap-2">
            {!item.assigneeId && !item.assignee && (
              <Chip tone="warn" label="Unassigned" />
            )}
            {breached && <Chip tone="danger" label="Breached" />}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto co-scrollbar-none px-[22px] py-[18px]">
        <p className="max-w-[74ch] text-[13.5px] leading-[1.65] text-[var(--co-ink-2)]">
          {body ? plainPreview(body) : 'No message body was captured for this conversation.'}
        </p>

        <div className="mt-[14px]">
          <Kv label="Customer" value={item.customer?.name || item.contactName || '—'} />
          <Kv
            label="Contact"
            value={item.customer?.email || item.contactEmail || '—'}
            verbatim
          />
          <Kv
            label="Status"
            value={`${item.status.replace(/_/g, ' ')} · ${item.priority}`}
          />
          <Kv
            label="SLA"
            value={slaLabel(item)}
            tone={breached ? 'danger' : slaTone(item) === 'warn' ? 'warn' : undefined}
          />
          <Kv label="Assignee" value={item.assignee?.name || 'Unassigned'} />
        </div>
      </div>

      <div
        className="flex flex-none items-center gap-[9px] border-t border-[var(--co-line)] bg-[var(--co-selected)] px-[22px] py-[14px]"
        style={{ borderRadius: '0 0 9px 9px' }}
      >
        <button
          type="button"
          onClick={onAssign}
          disabled={busy || !canAssign || assignedToMe}
          className={CONTROL}
        >
          {assignedToMe ? 'Assigned to you' : 'Assign to me'}
        </button>
        <div className="flex-1" />
        <button type="button" onClick={() => onOpen(item.id)} className={PRIMARY}>
          Open ticket →
        </button>
      </div>
    </section>
  );
}

function Chip({ tone, label }: { tone: 'warn' | 'danger'; label: string }) {
  const style =
    tone === 'danger'
      ? {
          borderColor: 'var(--co-burgundy-line)',
          background: 'var(--co-burgundy-wash)',
          color: 'var(--co-burgundy)',
        }
      : {
          borderColor: 'var(--co-terracotta-line)',
          background: 'var(--co-terracotta-wash)',
          color: 'var(--co-terracotta-ink)',
        };
  return (
    <span
      className="co-mono inline-flex items-center whitespace-nowrap rounded-md border px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em]"
      style={style}
    >
      {label}
    </span>
  );
}

function Kv({
  label,
  value,
  tone,
  verbatim,
}: {
  label: string;
  value: string;
  tone?: 'warn' | 'danger';
  /** An email address is not a title — print it as stored. */
  verbatim?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-3 border-b border-[var(--co-line-subtle)] py-[9px]">
      <span className="co-mono w-[98px] flex-none text-[9.5px] font-semibold uppercase tracking-[.12em] text-[var(--co-ink-3)]">
        {label}
      </span>
      <span
        className={clsx('min-w-0 flex-1 truncate text-[12.5px]', !verbatim && 'capitalize')}
        style={{
          color:
            tone === 'danger'
              ? 'var(--co-burgundy)'
              : tone === 'warn'
                ? 'var(--co-terracotta-ink)'
                : 'var(--co-ink)',
        }}
      >
        {value}
      </span>
    </div>
  );
}
